import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { theme } from '../theme';
import { subscribeActionGuard, resolveActionGuard, rejectActionGuard } from '../utils/actionGuardBus';

// Action Guard prompt. Two faces depending on the tenant config:
//
//   PIN method:         shows a 4-digit input. Server resolves the actor
//                       via HMAC index — no name picker.
//
//   Fingerprint method: shows a "Touch sensor" status card. The OS owns
//                       the actual biometric prompt — this modal just
//                       lets the user know what to do and provides a
//                       cancel button. The bus auto-resolves on success
//                       or auto-rejects on failure / cancel.
export default function ActionGuardPrompt({ API_BASE }) {
  const [ctx, setCtx] = useState(null);   // { actionKey, method }
  const [pin, setPin] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    return subscribeActionGuard((c) => {
      setCtx(c);
      setPin(''); setErr('');
    });
  }, []);

  const close = () => { setCtx(null); rejectActionGuard(); };

  const submitPin = async () => {
    if (!/^\d{4}$/.test(String(pin))) return setErr('4-digit PIN daalo');
    setBusy(true); setErr('');
    try {
      await axios.post(`${API_BASE}/action-guard/verify`, { pin });
      setCtx(null);
      resolveActionGuard({ pin });
    } catch (e) {
      setErr(e.response?.data?.error || 'Galat PIN');
    }
    setBusy(false);
  };

  if (!ctx) return null;

  const isFingerprint = ctx.method === 'fingerprint';

  return (
    <div onClick={close} style={{
      position: 'fixed', inset: 0, background: 'rgba(8,12,24,0.65)',
      zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 14, width: '100%', maxWidth: 400,
        padding: 26, boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
      }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#B00020' }}>Action guard</div>

        {isFingerprint ? (
          <>
            <h3 style={{ margin: '4px 0 12px', color: '#111', fontSize: '1.12rem' }}>Touch sensor karo 👆</h3>
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <div style={{
                width: 80, height: 80, borderRadius: '50%',
                background: 'linear-gradient(135deg,#0A7B3D,#0e9c4d)',
                margin: '0 auto 16px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 0 0 0 rgba(10,123,61,0.4)',
                animation: 'oralzy-ag-pulse 1.5s infinite',
              }}>
                <span style={{ fontSize: 36 }}>👆</span>
              </div>
              <p style={{ fontSize: '0.92rem', color: '#111', margin: '0 0 6px', fontWeight: 600 }}>
                Apna fingerprint touch karo
              </p>
              <p style={{ fontSize: '0.78rem', color: theme.textSubtle, margin: 0 }}>
                Touch ID / Windows Hello / Android sensor — jo bhi laptop/phone me hai.
              </p>
            </div>
            <style>{`
              @keyframes oralzy-ag-pulse {
                0% { box-shadow: 0 0 0 0 rgba(10,123,61,0.5); }
                70% { box-shadow: 0 0 0 18px rgba(10,123,61,0); }
                100% { box-shadow: 0 0 0 0 rgba(10,123,61,0); }
              }
            `}</style>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={close}
                style={{ padding: '8px 16px', background: '#fff', color: '#333', border: `1px solid ${theme.border}`, borderRadius: 7, fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </>
        ) : (
          <>
            <h3 style={{ margin: '4px 0 12px', color: '#111', fontSize: '1.12rem' }}>Apna PIN daalo</h3>
            <p style={{ fontSize: '0.84rem', color: theme.textSubtle, marginBottom: 14 }}>
              Sensitive action — apna 4-digit Action PIN daalo. Audit log me asli naam record hoga.
              PIN bhul gaye? Admin se naya PIN request karo.
            </p>
            <input type="password" inputMode="numeric" pattern="[0-9]*" autoFocus
              value={pin} onChange={e => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
              onKeyDown={e => { if (e.key === 'Enter') submitPin(); }}
              maxLength={4} placeholder="• • • •"
              style={{ width: '100%', padding: '14px 13px', border: `1px solid ${theme.border}`, borderRadius: 7, fontSize: '1.6rem', letterSpacing: '0.6em', textAlign: 'center', fontFamily: 'monospace', boxSizing: 'border-box', marginBottom: 12 }}/>
            {err && <div style={{ fontSize: '0.82rem', color: '#B00020', marginBottom: 10 }}>{err}</div>}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10 }}>
              <button onClick={close} disabled={busy}
                style={{ padding: '9px 18px', background: '#fff', color: '#333', border: `1px solid ${theme.border}`, borderRadius: 7, fontWeight: 600, fontSize: '0.85rem', cursor: busy ? 'wait' : 'pointer' }}>
                Cancel
              </button>
              <button onClick={submitPin} disabled={busy || pin.length !== 4}
                style={{ padding: '9px 18px', background: (!busy && pin.length === 4) ? '#0A7B3D' : '#bfd8c8', color: '#fff', border: 'none', borderRadius: 7, fontWeight: 700, fontSize: '0.85rem', cursor: busy ? 'wait' : 'pointer' }}>
                {busy ? 'Checking…' : 'Confirm'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
